import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
    Avatar,
    Box,
    ListItemButton,
    MenuItem,
    Popper,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material'
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined'
import NightlightOutlinedIcon from '@mui/icons-material/NightlightOutlined'
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { AuthHandlerContext } from '../../App'
import { selectUserDetails, selectPrimaryTradingAccount, selectMargins } from '../../selectors/user'
import { selectThemeMode } from '../../selectors/common'
import commonActions from '../../actions/common.actions'
import AppearanceIcon from '../../assets/icons/AppearanceIcon'
import { white } from '../../assets/colors'
import { getAccountTabPath } from '../../pages/settings/accountRoutes'
import { getLoginPalette } from '../../pages/login/loginTheme'
import { SIDE_BAR_BOTTOM_ICON_SIZE } from '../sideBarRight/sideBarBottomIconButtonSx'

const PROFILE_ROW_HEIGHT = 56

interface UserProfileMenuProps {
    hovered: boolean
    onCloseSidebar?: () => void
}

const MENU_ROW_SX = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    minHeight: 44,
    py: 1.25,
    px: 2,
    m: 0,
    borderRadius: 0
}

const MENU_ICON_SX = { fontSize: 20, width: 20, height: 20, color: 'text.secondary' }

const formatAvailableFunds = (value: number | undefined) => {
    if (value === undefined) return '—'
    return `₹ ${value.toLocaleString('en-IN')}`
}

export function UserProfileMenu({ hovered, onCloseSidebar }: UserProfileMenuProps) {
    const authHandlerContext = useContext(AuthHandlerContext)
    const userDetails = useSelector(selectUserDetails)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const margins = useSelector(selectMargins)
    const themeMode = useSelector(selectThemeMode)
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [menuHovered, setMenuHovered] = useState(false)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [userSelectedMode, setUserSelectedMode] = useState<'Light' | 'Dark' | 'System'>()
    const [avatarError, setAvatarError] = useState(false)

    const avatarUrl = userDetails.currentUser?.avatarUrl
    const showUserIcon = !avatarUrl || avatarError
    const userInitial = userDetails.currentUser?.name?.trim().charAt(0).toUpperCase()

    const availableFunds = useMemo(() => {
        const accountId = primaryTradingAccount?.accountId
        if (!accountId) return undefined
        return margins[accountId]?.marginAvailable
    }, [primaryTradingAccount?.accountId, margins])

    const checkSystemTheme = useCallback(() => {
        const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches
        const systemTheme = isDarkMode ? 'Dark' : 'Light'
        const savedTheme = localStorage.getItem('themeMode') || 'Dark'
        const finalTheme = savedTheme === 'System' ? systemTheme : savedTheme
        commonActions.setThemeMode(dispatch, finalTheme as 'Light' | 'Dark' | 'System')
    }, [dispatch])

    const handleThemeChange = useCallback((mode: 'Light' | 'Dark' | 'System') => {
        setUserSelectedMode(mode)
        commonActions.setThemeMode(dispatch, mode)
        localStorage.setItem('themeMode', mode)
    }, [dispatch])

    useEffect(() => {
        setAvatarError(false)
    }, [avatarUrl])

    useEffect(() => {
        checkSystemTheme()
    }, [checkSystemTheme])

    useEffect(() => {
        const handleFocus = () => {
            checkSystemTheme()
        }

        window.addEventListener('focus', handleFocus)
        return () => {
            window.removeEventListener('focus', handleFocus)
        }
    }, [checkSystemTheme])

    useEffect(() => {
        if (!hovered) {
            setMenuHovered(false)
            setAnchorEl(null)
        }
    }, [hovered])

    const closeMenu = useCallback(() => {
        setMenuHovered(false)
        setAnchorEl(null)
        onCloseSidebar?.()
    }, [onCloseSidebar])

    const handleProfileClick = useCallback(() => {
        navigate(getAccountTabPath('personal'))
        closeMenu()
    }, [navigate, closeMenu])

    const handleFundsClick = useCallback(() => {
        navigate(getAccountTabPath('funds'))
        closeMenu()
    }, [navigate, closeMenu])

    const handleSignOut = useCallback(() => {
        authHandlerContext.onLoggedOut()
        closeMenu()
    }, [authHandlerContext, closeMenu])

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    height: PROFILE_ROW_HEIGHT,
                    minHeight: PROFILE_ROW_HEIGHT
                }}
                onMouseEnter={(e) => {
                    setMenuHovered(true)
                    setAnchorEl(e.currentTarget)
                }}
                onMouseLeave={() => setMenuHovered(false)}
            >
                <ListItemButton
                    data-event="Profile Icon Clicked"
                    onClick={handleProfileClick}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        textTransform: 'none',
                        width: '100%',
                        height: PROFILE_ROW_HEIGHT,
                        minHeight: PROFILE_ROW_HEIGHT,
                        py: 0,
                        px: 2,
                        boxSizing: 'border-box',
                        backgroundColor: menuHovered
                            ? (t) => t.palette.background.default
                            : 'transparent',
                        '&:hover': {
                            backgroundColor: (t) => t.palette.background.default
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <Avatar
                            alt={userDetails.currentUser?.name}
                            src={showUserIcon ? undefined : avatarUrl}
                            // Google profile photos can 403 when a Referer is sent.
                            imgProps={{ onError: () => setAvatarError(true), referrerPolicy: 'no-referrer' }}
                            sx={(t) => {
                                // Same look as the PIN screen avatar.
                                const p = getLoginPalette(t.palette.mode === 'dark')
                                return {
                                    // Same size as the right sidebar's bottom icons.
                                    width: SIDE_BAR_BOTTOM_ICON_SIZE,
                                    height: SIDE_BAR_BOTTOM_ICON_SIZE,
                                    bgcolor: p.surface2,
                                    color: p.secondary,
                                    border: `1px solid ${p.border}`,
                                    fontSize: '13px',
                                    fontWeight: 500,
                                }
                            }}
                        >
                            {/* Empty until the profile loads, so no icon flashes before the initial.
                                An empty span (not false/null): MUI Avatar draws its own person icon when children are empty. */}
                            {showUserIcon && (userDetails.currentUser ? userInitial || <PersonOutlineIcon sx={{ fontSize: 18 }} /> : <span />)}
                        </Avatar>
                    </Box>
                    {hovered && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                marginLeft: '10px',
                                width: '190px',
                                overflow: 'hidden',
                                minWidth: 0
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    flexGrow: 1,
                                    minWidth: 0
                                }}
                            >
                                <Typography
                                    fontSize={12}
                                    lineHeight={1.25}
                                    color="textPrimary"
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        width: '100%'
                                    }}
                                >
                                    {userDetails.currentUser?.name}
                                </Typography>
                                <Typography
                                    color="textSecondary"
                                    fontSize={12}
                                    lineHeight={1.25}
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        width: '100%'
                                    }}
                                >
                                    {userDetails.currentUser?.email}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </ListItemButton>
            </Box>

            <Popper
                open={menuHovered && Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="right-end"
                onMouseEnter={() => setMenuHovered(true)}
                onMouseLeave={() => {
                    setMenuHovered(false)
                    setAnchorEl(null)
                }}
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [-2, 0]
                        }
                    }
                ]}
                sx={{
                    bgcolor: (t) => (t.palette.mode === 'dark' ? '#101010' : white),
                    p: 0,
                    borderRadius: 1,
                    boxShadow: 3,
                    zIndex: 1300,
                    pointerEvents: 'auto'
                }}
            >
                <Box sx={{ minWidth: 220, py: 0.5 }}>
                    <Box
                        data-event="Theme Changed"
                        data-event-theme-selected={themeMode}
                        sx={{
                            ...MENU_ROW_SX,
                            justifyContent: 'flex-start',
                            '&:hover': { bgcolor: (t) => t.palette.action.hover }
                        }}
                    >
                        <AppearanceIcon sx={MENU_ICON_SX} />
                        <Typography fontSize={14} sx={{ whiteSpace: 'nowrap' }}>
                            {themeMode}
                        </Typography>
                        <ToggleButtonGroup
                            value={themeMode}
                            exclusive
                            onChange={(_, mode) => mode && handleThemeChange(mode)}
                            sx={{
                                ml: 'auto',
                                height: '28px',
                                alignItems: 'center',
                                '.MuiButtonBase-root': {
                                    borderColor: (t) => t.palette.divider
                                },
                                '&:not(:first-of-type)': {
                                    marginLeft: (t) => t.spacing(0.5)
                                }
                            }}
                        >
                            <ToggleButton
                                value="Light"
                                selected={userSelectedMode === 'Light' && themeMode !== 'System'}
                                sx={{
                                    color: (t) =>
                                        themeMode === 'Light' && userSelectedMode !== 'System'
                                            ? t.palette.text.primary
                                            : t.palette.text.secondary,
                                    height: '22px',
                                    width: '28px',
                                    minWidth: '28px',
                                    borderRadius: '2px 0 0 2px'
                                }}
                            >
                                <LightModeOutlinedIcon sx={{ fontSize: 16 }} />
                            </ToggleButton>
                            <ToggleButton
                                value="Dark"
                                selected={userSelectedMode === 'Dark' && themeMode !== 'System'}
                                sx={{
                                    color: (t) =>
                                        themeMode === 'Dark' && userSelectedMode !== 'System'
                                            ? t.palette.text.primary
                                            : t.palette.text.secondary,
                                    height: '22px',
                                    width: '28px',
                                    minWidth: '28px'
                                }}
                            >
                                <NightlightOutlinedIcon sx={{ fontSize: 16 }} />
                            </ToggleButton>
                            <ToggleButton
                                value="System"
                                selected={userSelectedMode === 'System'}
                                sx={{
                                    color: (t) =>
                                        userSelectedMode === 'System' ? t.palette.text.primary : t.palette.text.secondary,
                                    height: '22px',
                                    width: '28px',
                                    minWidth: '28px',
                                    borderRadius: '0 2px 2px 0'
                                }}
                            >
                                <AutoFixHighOutlinedIcon sx={{ fontSize: 16 }} />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <MenuItem onClick={handleFundsClick} sx={MENU_ROW_SX}>
                        <AccountBalanceWalletOutlinedIcon sx={MENU_ICON_SX} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography fontSize={14} lineHeight={1.2}>Funds</Typography>
                            <Typography fontSize={12} color="text.secondary" lineHeight={1.2}>
                                {formatAvailableFunds(availableFunds)} available
                            </Typography>
                        </Box>
                    </MenuItem>

                    <MenuItem data-event="Signed Out" onClick={handleSignOut} sx={MENU_ROW_SX}>
                        <LogoutOutlinedIcon sx={MENU_ICON_SX} />
                        <Typography fontSize={14}>Sign out</Typography>
                    </MenuItem>
                </Box>
            </Popper>
        </>
    )
}
